import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { APERTUM_CHAIN_ID } from './apertumChain';
import { isAdminWallet } from './adminWallets';
import { readOnChainAdmin } from './registryClient';
import {
  adminBypassEligibility,
  type DidEligibility,
  verifyGlobalDidAccess,
} from './didEligibility';
import { createJsonRpcDidReader } from './ethersDidReader';
import {
  ADMIN_LOGIN_STATEMENT,
  WALLET_LOGIN_STATEMENT,
  buildLoginMessage,
  clearStoredClaim,
  isClaimValid,
  issueClaim,
  randomNonce,
  readStoredClaim,
  writeStoredClaim,
  type Web3Claim,
  type Web3ClaimKind,
} from './siweSession';
import { useUnifiedWallet } from '../hooks/useUnifiedWallet';
import type { DiscoveredWallet as UnifiedDiscoveredWallet } from '../hooks/useWalletDiscovery';
import {
  type DiscoveredWallet,
  type Eip1193Provider,
  recoverPersonalSigner,
  signPersonalMessage,
} from './walletConnect';

export type Web3LoginStep =
  | 'disconnected'
  | 'wrong_network'
  | 'connected'
  | 'needs_signature'
  | 'signed_in';

export interface Web3AuthState {
  address: string | null;
  chainId: number | null;
  isCorrectChain: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  isSigningIn: boolean;
  claim: Web3Claim | null;
  eligibility: DidEligibility | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  loginStep: Web3LoginStep;
  error: string | null;
  availableWallets: DiscoveredWallet[];
  getRawProvider: () => Eip1193Provider | null;
  connect: (wallet?: DiscoveredWallet | UnifiedDiscoveredWallet) => Promise<void>;
  switchNetwork: () => Promise<void>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshEligibility: () => Promise<DidEligibility | null>;
}

const EMPTY_WALLETS: DiscoveredWallet[] = [];

const Web3AuthContext = createContext<Web3AuthState | null>(null);

export function useWeb3Auth(): Web3AuthState {
  const ctx = useContext(Web3AuthContext);
  if (!ctx) throw new Error('useWeb3Auth must be used inside Web3AuthProvider');
  return ctx;
}

export function useWeb3AuthOptional(): Web3AuthState | null {
  return useContext(Web3AuthContext);
}

export function Web3AuthProvider({ children }: { children: ReactNode }) {
  const unified = useUnifiedWallet();
  const address = unified.address;
  const chainId = unified.chainId;
  const isConnecting = unified.isConnecting;
  const isCorrectChain = unified.isCorrectChain;
  const isConnected = unified.isConnected;
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [claim, setClaim] = useState<Web3Claim | null>(() => readStoredClaim());
  const [eligibility, setEligibility] = useState<DidEligibility | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<Eip1193Provider | null>(null);
  const availableWallets = EMPTY_WALLETS;
  const [onChainAdmin, setOnChainAdmin] = useState(false);
  const isAuthenticated = Boolean(
    address &&
      isClaimValid(claim) &&
      claim!.address.toLowerCase() === address.toLowerCase()
  );
  const canUseAdminSession = isAdminWallet(address) || onChainAdmin;
  const isAdmin = Boolean(canUseAdminSession && isAuthenticated);

  const loginStep: Web3LoginStep = !isConnected
    ? 'disconnected'
    : !isCorrectChain
      ? 'wrong_network'
      : isAuthenticated
        ? 'signed_in'
        : 'needs_signature';

  const refreshEligibility = useCallback(async () => {
    if (!address || !isCorrectChain) {
      setEligibility(null);
      return null;
    }
    if (isAdminWallet(address)) {
      const bypass = adminBypassEligibility();
      setEligibility(bypass);
      return bypass;
    }
    const { reader } = createJsonRpcDidReader();
    const result = await verifyGlobalDidAccess({ wallet: address, reader });
    setEligibility((prev) => (prev?.reason === 'admin_bypass' ? prev : result));
    return result;
  }, [address, isCorrectChain]);

  useEffect(() => {
    setProvider((unified.getRawProvider() as Eip1193Provider | null) ?? null);
  }, [unified.address, unified.isConnected, unified.chainId, unified.getRawProvider]); // eslint-disable-line react-hooks/exhaustive-deps -- getRawProvider is a stable module fn

  useEffect(() => {
    if (!address || !claim) return;
    if (claim.address.toLowerCase() !== address.toLowerCase()) {
      clearStoredClaim();
      setClaim(null);
    }
  }, [address, claim]);

  const connect = useCallback(
    async (wallet?: DiscoveredWallet | UnifiedDiscoveredWallet) => {
      setError(null);
      if (!wallet || !('type' in wallet)) {
        throw new Error('Select a wallet from the connect modal.');
      }
      await unified.connect(wallet);
    },
    [unified]
  );

  const switchNetwork = useCallback(async () => {
    setError(null);
    await unified.switchToApertum();
  }, [unified]);

  const signIn = useCallback(async () => {
    if (!address || !provider) throw new Error('Connect a wallet first.');
    if (!isCorrectChain) throw new Error('Switch to Apertum first.');
    setIsSigningIn(true);
    setError(null);
    try {
      const adminSession = isAdminWallet(address) || onChainAdmin;
      let didId = 0;
      let kind: Web3ClaimKind = 'wallet';
      let statement: string | undefined = WALLET_LOGIN_STATEMENT;
      if (!adminSession) {
        const did = eligibility ?? (await refreshEligibility());
        if (did?.eligible && did.didId != null) {
          didId = did.didId;
          kind = 'did';
          statement = undefined;
        }
      } else {
        kind = 'admin';
        statement = ADMIN_LOGIN_STATEMENT;
      }
      const message = buildLoginMessage({
        domain: typeof window !== 'undefined' ? window.location.host : 'sportsfuture',
        address,
        uri: typeof window !== 'undefined' ? window.location.origin : 'https://sportsfuture.io',
        chainId: APERTUM_CHAIN_ID,
        nonce: randomNonce(),
        issuedAt: new Date().toISOString(),
        statement,
      });
      const signature = await signPersonalMessage(provider, address, message);
      const recovered = recoverPersonalSigner(message, signature);
      if (recovered.toLowerCase() !== address.toLowerCase()) {
        throw new Error('Signature does not match the connected wallet.');
      }
      const next = issueClaim({
        address,
        didId,
        kind,
        message,
        signature,
      });
      writeStoredClaim(next);
      setClaim(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
      throw err;
    } finally {
      setIsSigningIn(false);
    }
  }, [address, eligibility, isCorrectChain, onChainAdmin, provider, refreshEligibility]);

  const signOut = useCallback(async () => {
    clearStoredClaim();
    setClaim(null);
    setEligibility(null);
    setError(null);
    await unified.disconnect();
  }, [unified]);

  useEffect(() => {
    if (address && isCorrectChain) {
      if (!isAdminWallet(address)) {
        void refreshEligibility();
      } else {
        setEligibility(adminBypassEligibility());
      }
      void readOnChainAdmin(chainId ?? APERTUM_CHAIN_ID, address)
        .then((ok) => {
          setOnChainAdmin(ok);
          if (ok) setEligibility(adminBypassEligibility());
        })
        .catch(() => setOnChainAdmin(false));
    } else {
      setOnChainAdmin(false);
    }
  }, [address, chainId, isCorrectChain, refreshEligibility]);

  // Signature is click-gated in ConnectWalletButton ("Sign to continue").
  // Do not call signIn() from an effect on address — that pops a wallet
  // prompt as soon as a visitor lands, including after a silent reconnect.

  useEffect(() => {
    const stored = readStoredClaim();
    if (stored && stored.eligibleUntil <= Date.now()) {
      clearStoredClaim();
      setClaim(null);
    }
  }, []);

  const value = useMemo<Web3AuthState>(
    () => ({
      address,
      chainId,
      isCorrectChain,
      isConnected,
      isConnecting,
      isSigningIn,
      claim,
      eligibility,
      isAuthenticated,
      isAdmin,
      loginStep,
      error,
      availableWallets,
      getRawProvider: () => provider,
      connect,
      switchNetwork,
      signIn,
      signOut,
      refreshEligibility,
    }),
    [
      address,
      availableWallets,
      chainId,
      claim,
      connect,
      eligibility,
      error,
      isAdmin,
      isAuthenticated,
      isConnected,
      isConnecting,
      isCorrectChain,
      isSigningIn,
      loginStep,
      refreshEligibility,
      signIn,
      signOut,
      switchNetwork,
      provider,
    ]
  );

  return <Web3AuthContext.Provider value={value}>{children}</Web3AuthContext.Provider>;
}
